import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { DwMetaDataService, DwMetaDataServiceToken, DwQueryFilterGroup, DwQueryHelper } from '@devwareapps/devware-cap';
import { combineLatest } from 'rxjs';
import { ActiveFilter } from '../../models/active-filter.model';
import { FilterConfig } from '../../models/filter-config.model';
import { FilterType } from '../../models/filter-type.enum';
import { ensureArray } from '../../../shared/util/ensure-array.util';

@Component({
  selector: 'app-generic-tag-filters',
  templateUrl: './generic-tag-filters.component.html',
  styleUrls: ['./generic-tag-filters.component.scss']
})
export class GenericTagFiltersComponent implements OnInit {
  @Input() filterConfigs: FilterConfig[] = [];
  @Input() autoRefresh = true;
  @Output() filtersChanged = new EventEmitter<DwQueryFilterGroup>();

  activeFilters: ActiveFilter[] = [];
  availableFilters: FilterConfig[] = [];
  showFilterPanel = false;
  selectedFilterConfig: FilterConfig = null;

  FilterType = FilterType;

  // Temporary values for filter controls
  tempFilterValue: any = null;
  tempDateRangeStart: Date = null;
  tempDateRangeEnd: Date = null;

  lookupParams: any[] = [];

  constructor(@Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService) { }

  ngOnInit(): void {
    this.updateAvailableFilters();
  }

  openFilterPanel(): void {
    this.showFilterPanel = true;
  }

  closeFilterPanel(): void {
    this.showFilterPanel = false;
    this.selectedFilterConfig = null;
    this.resetTempValues();
  }

  onFilterSelected(config: FilterConfig): void {
    this.selectedFilterConfig = config;
    this.tempFilterValue = config.defaultValue;

    if (config.type === FilterType.Toggle) {
      this.tempFilterValue = this.tempFilterValue !== undefined ? this.tempFilterValue : true;
      this.applySelectedFilter();
    }
  }

  applySelectedFilter(): void {
    if (!this.selectedFilterConfig) {
      return;
    }

    let value: any;
    let displayValue = '';

    switch (this.selectedFilterConfig.type) {
      case FilterType.DateRange:
        if (!this.tempDateRangeStart || !this.tempDateRangeEnd) {
          return;
        }
        value = {
          start: this.tempDateRangeStart,
          end: this.tempDateRangeEnd
        };
        displayValue = `${this.formatDate(this.tempDateRangeStart)} - ${this.formatDate(this.tempDateRangeEnd)}`;
        break;

      case FilterType.DatePicker:
        value = this.tempFilterValue;
        displayValue = this.formatDate(this.tempFilterValue);
        break;

      case FilterType.Toggle:
        value = this.tempFilterValue;
        displayValue = value ? 'Yes' : 'No';
        break;

      case FilterType.Dropdown:
      case FilterType.MultiSelect:
        value = this.tempFilterValue;

        if (this.selectedFilterConfig.options) {
          displayValue = this.getDisplayValueForDropdown(value, this.selectedFilterConfig);
          break;
        }

        if (this.selectedFilterConfig.lookupId) {
          const values = ensureArray(value);
          const lookupId = this.selectedFilterConfig.lookupId!;
          const lookups = values.map(v => this.dwMetaDataService.getSingleLookupItem(lookupId, v));

          combineLatest(lookups).subscribe(lookupItems => {
            const labels = lookupItems.map((li, idx) => li?.Display || values[idx]);
            const resolvedDisplay = labels.join(', ');

            const existingIndex2 = this.activeFilters.findIndex(f => f.field === this.selectedFilterConfig.field);
            if (existingIndex2 >= 0) {
              this.activeFilters[existingIndex2] = {
                field: this.selectedFilterConfig.field,
                value: value,
                displayLabel: this.selectedFilterConfig.label,
                displayValue: resolvedDisplay
              };
            } else {
              this.activeFilters.push({
                field: this.selectedFilterConfig.field,
                value: value,
                displayLabel: this.selectedFilterConfig.label,
                displayValue: resolvedDisplay
              });
            }

            this.closeFilterPanel();
            this.updateAvailableFilters();
            this.emitFiltersChanged();
          });

          return;
        }

        displayValue = ensureArray(value).join(', ');
        break;

      case FilterType.TextInput:
        value = this.tempFilterValue;
        displayValue = value;
        break;

      default:
        value = this.tempFilterValue;
        displayValue = value?.toString() || '';
    }

    if (value === null || value === undefined || value === '') {
      return;
    }

    const existingIndex = this.activeFilters.findIndex(f => f.field === this.selectedFilterConfig.field);
    if (existingIndex >= 0) {
      this.activeFilters[existingIndex] = {
        field: this.selectedFilterConfig.field,
        value: value,
        displayLabel: this.selectedFilterConfig.label,
        displayValue: displayValue
      };
    } else {
      this.activeFilters.push({
        field: this.selectedFilterConfig.field,
        value: value,
        displayLabel: this.selectedFilterConfig.label,
        displayValue: displayValue
      });
    }

    this.closeFilterPanel();
    this.updateAvailableFilters();
    this.emitFiltersChanged();
  }

  removeFilter(filter: ActiveFilter): void {
    this.activeFilters = this.activeFilters.filter(f => f.field !== filter.field);
    this.updateAvailableFilters();
    this.emitFiltersChanged();
  }

  clearAllFilters(): void {
    this.activeFilters = [];
    this.updateAvailableFilters();
    this.emitFiltersChanged();
  }

  private updateAvailableFilters(): void {
    const activeFields = this.activeFilters.map(f => f.field);
    this.availableFilters = this.filterConfigs.filter(config => !activeFields.includes(config.field));
  }

  private emitFiltersChanged(): void {
    const filterGroup = new DwQueryFilterGroup();
    const qh = new DwQueryHelper();

    this.activeFilters.forEach(activeFilter => {
      const config = this.filterConfigs.find(c => c.field === activeFilter.field);
      if (!config || !config.attribute) {
        return;
      }

      const attrName = config.attribute.fieldNameWithItemPrefix;

      switch (config.type) {
        case FilterType.DateRange:
          if (activeFilter.value.start && activeFilter.value.end) {
            filterGroup.AddFilterAnd(qh.filters.GreaterThanEqual(attrName, activeFilter.value.start));
            filterGroup.AddFilterAnd(qh.filters.LessThanEqual(attrName, activeFilter.value.end));
          }
          break;

        case FilterType.DatePicker:
          filterGroup.AddFilterAnd(qh.filters.Equal(attrName, activeFilter.value));
          break;

        case FilterType.Toggle:
          filterGroup.AddFilterAnd(qh.filters.Equal(attrName, activeFilter.value));
          break;

        case FilterType.Dropdown:
        case FilterType.MultiSelect:
          const values = ensureArray(activeFilter.value);
          filterGroup.AddFilterAnd(qh.filters.InList(attrName, values));
          break;

        case FilterType.TextInput:
          filterGroup.AddFilterAnd(qh.filters.Contains(attrName, activeFilter.value));
          break;
      }
    });

    this.filtersChanged.emit(filterGroup);
  }

  private getDisplayValueForDropdown(value: any, config: FilterConfig): string {
    if (!value) {
      return '';
    }

    const values = ensureArray(value);

    if (config.options) {
      const opts = config.options || [];
      const labels = values.map(v => {
        const option = opts.find(o => o.value === v);
        return option ? option.label : v;
      });
      return labels.join(', ');
    }

    return values.join(', ');
  }

  private formatDate(date: Date): string {
    if (!date) {
      return '';
    }

    const d = new Date(date);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  }

  private resetTempValues(): void {
    this.tempFilterValue = null;
    this.tempDateRangeStart = null;
    this.tempDateRangeEnd = null;
  }
}
